/**
 * Blossom file upload with XHR progress reporting (BUD-01 / BUD-02).
 *
 * Uploads to multiple Blossom servers simultaneously and reports both
 * overall progress and per-server progress so the UI can render a bar for
 * each. Large video files are never loaded fully into RAM — XHR streams
 * the File/Blob directly.
 */

export interface UploadProgressEvent {
  loaded: number;   // bytes uploaded so far
  total: number;    // total file size in bytes
  percent: number;  // 0–100
}

export interface BlobDescriptor {
  url: string;
  sha256: string;
  size: number;
  type?: string;
}

export interface ServerUploadResult {
  serverUrl: string;
  success: boolean;
  url?: string;
  error?: string;
}

export type ServerProgressStatus = 'pending' | 'uploading' | 'done' | 'failed';

export interface ServerProgress {
  serverUrl: string;
  status: ServerProgressStatus;
  progress: UploadProgressEvent;
  error?: string;
}

/** Minimal signer interface (NIP-07 / NSecSigner compatible). */
export interface BlossomSigner {
  signEvent(event: {
    kind: number;
    content: string;
    tags: string[][];
    created_at: number;
  }): Promise<{ id: string; pubkey: string; sig: string; kind: number; content: string; tags: string[][]; created_at: number }>;
}

/** Compute SHA-256 of an ArrayBuffer and return the lowercase hex string. */
export async function sha256HexFromBuffer(buffer: ArrayBuffer): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/** Compute SHA-256 of a File/Blob. */
export async function sha256HexFromFile(file: File | Blob): Promise<string> {
  const arrayBuffer = await new Response(file).arrayBuffer();
  return sha256HexFromBuffer(arrayBuffer);
}

/** Build and sign a Blossom kind-24242 upload authorization token (Base64url). */
async function buildAuthToken(
  signer: BlossomSigner,
  fileHash: string,
  serverUrl: string,
): Promise<string> {
  const expiration = Math.floor(Date.now() / 1000) + 60 * 60; // 1 hour
  const serverHost = new URL(serverUrl).hostname;

  const event = await signer.signEvent({
    kind: 24242,
    content: 'Upload Blob',
    created_at: Math.floor(Date.now() / 1000),
    tags: [
      ['t', 'upload'],
      ['x', fileHash],
      ['expiration', expiration.toString()],
      ['server', serverHost],
    ],
  });

  const json = JSON.stringify(event);
  const b64 = btoa(unescape(encodeURIComponent(json)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  return b64;
}

/** Upload a small file (e.g. thumbnail) to multiple Blossom servers using fetch. */
export async function uploadFileToAllServers(
  file: File,
  servers: string[],
  signer: BlossomSigner,
): Promise<{ url: string; sha256: string; serverResults: ServerUploadResult[] }> {
  if (servers.length === 0) throw new Error('No servers provided');

  const buffer = await file.arrayBuffer();
  const fileHash = await sha256HexFromBuffer(buffer);
  const contentType = file.type || 'application/octet-stream';

  const serverResults: ServerUploadResult[] = await Promise.all(
    servers.map(async (serverUrl): Promise<ServerUploadResult> => {
      try {
        const authToken = await buildAuthToken(signer, fileHash, serverUrl);
        const uploadUrl = serverUrl.replace(/\/?$/, '/') + 'upload';
        const res = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Authorization': `Nostr ${authToken}`,
            'Content-Type': contentType,
            'X-SHA-256': fileHash,
          },
          body: buffer.slice(0),
        });
        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          try { const b = await res.json(); if (b.message) msg = b.message; } catch { /* ignore */ }
          return { serverUrl, success: false, error: msg };
        }
        const descriptor: BlobDescriptor = await res.json();
        return { serverUrl, success: true, url: descriptor.url };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return { serverUrl, success: false, error: msg };
      }
    })
  );

  const first = serverResults.find(r => r.success && r.url);
  if (!first?.url) {
    const errors = serverResults.map(r => `${r.serverUrl}: ${r.error}`).join('; ');
    throw new Error(`All server uploads failed: ${errors}`);
  }
  return { url: first.url, sha256: fileHash, serverResults };
}

/** Upload a File to a single Blossom server via XHR with progress reporting. */
async function uploadFileToServer(
  file: File,
  contentType: string,
  serverUrl: string,
  fileHash: string,
  authToken: string,
  onProgress: (e: UploadProgressEvent) => void,
): Promise<BlobDescriptor> {
  const uploadUrl = serverUrl.replace(/\/?$/, '/') + 'upload';

  return new Promise<BlobDescriptor>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        onProgress({
          loaded: e.loaded,
          total: e.total,
          percent: Math.round((e.loaded / e.total) * 100),
        });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const descriptor: BlobDescriptor = JSON.parse(xhr.responseText);
          resolve(descriptor);
        } catch {
          reject(new Error(`Blossom server returned invalid JSON: ${xhr.responseText}`));
        }
      } else {
        let msg = `Upload failed: ${xhr.status}`;
        try {
          const body = JSON.parse(xhr.responseText);
          if (body.message) msg = body.message;
        } catch { /* ignore */ }
        reject(new Error(msg));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Authorization', `Nostr ${authToken}`);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.setRequestHeader('X-SHA-256', fileHash);
    xhr.send(file);
  });
}

export interface MultiServerUploadOptions {
  /** Called whenever any server's progress or status changes. */
  onServerProgress?: (server: ServerProgress, all: ServerProgress[]) => void;
}

/**
 * Upload a file to one or more Blossom servers simultaneously with XHR
 * progress reporting per server.
 */
export async function uploadWithProgress(
  file: File,
  serverUrls: string | string[],
  signer: BlossomSigner,
  options: MultiServerUploadOptions = {},
): Promise<BlobDescriptor & { serverResults: ServerUploadResult[] }> {
  const servers = (Array.isArray(serverUrls) ? serverUrls : [serverUrls]).filter(Boolean);

  if (servers.length === 0) {
    throw new Error('No servers provided for upload');
  }

  const contentType = file.type || 'application/octet-stream';
  const fileHash = await sha256HexFromFile(file);

  const authTokens = await Promise.all(
    servers.map(serverUrl => buildAuthToken(signer, fileHash, serverUrl))
  );

  // Per-server progress state
  const state = new Map<string, ServerProgress>(
    servers.map(url => [url, {
      serverUrl: url,
      status: 'pending' as const,
      progress: { loaded: 0, total: file.size, percent: 0 },
    }])
  );

  const emit = (updated: ServerProgress) => {
    options.onServerProgress?.(updated, Array.from(state.values()));
  };

  const serverResults: ServerUploadResult[] = await Promise.all(
    servers.map(async (serverUrl, index): Promise<ServerUploadResult> => {
      const current = state.get(serverUrl);
      if (current) {
        current.status = 'uploading';
        emit({ ...current });
      }
      try {
        const result = await uploadFileToServer(
          file, contentType, serverUrl, fileHash, authTokens[index],
          (progress) => {
            const entry = state.get(serverUrl);
            if (entry) {
              entry.progress = progress;
              entry.status = 'uploading';
              emit({ ...entry });
            }
          },
        );
        const entry = state.get(serverUrl);
        if (entry) {
          entry.status = 'done';
          entry.progress = { loaded: file.size, total: file.size, percent: 100 };
          emit({ ...entry });
        }
        return { serverUrl, success: true, url: result.url };
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        const entry = state.get(serverUrl);
        if (entry) {
          entry.status = 'failed';
          entry.error = msg;
          emit({ ...entry });
        }
        return { serverUrl, success: false, error: msg };
      }
    })
  );

  const successResult = serverResults.find(r => r.success && r.url);
  if (!successResult?.url) {
    const errors = serverResults.map(r => `${r.serverUrl}: ${r.error}`).join('; ');
    throw new Error(`All server uploads failed: ${errors}`);
  }

  return {
    url: successResult.url,
    sha256: fileHash,
    size: file.size,
    type: contentType,
    serverResults,
  };
}

/** Format bytes as a human-readable string (e.g. "12.4 MB / 250 MB"). */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
